import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { BookOpen, HelpCircle, Home, Tv, MonitorPlay, ShieldCheck, Menu, X, Users, Mail, BatteryCharging, Smartphone, Lock, ChevronDown, Gauge, Accessibility, CalendarDays } from "lucide-react";

const SERVICES = [
  { to: "/hemkurs/smart-tv", label: "Hemkurs Smart-TV", Icon: Tv, testid: "nav-course-smarttv" },
  { to: "/hemkurs/tv-box", label: "Hemkurs TV-box", Icon: MonitorPlay, testid: "nav-course-tvbox" },
  { to: "/sakerhet", label: "Säker på nätet", Icon: ShieldCheck, testid: "nav-security" },
  { to: "/tillganglighet", label: "Anpassad tillgänglighet", Icon: Accessibility, testid: "nav-access" },
  { to: "/batteribyte", label: "Batteribyte på dator", Icon: BatteryCharging, testid: "nav-battery" },
  { to: "/uppgradering", label: "Snabbare dator & mer lagring", Icon: Gauge, testid: "nav-upgrade" },
  { to: "/enhetsbyte", label: "Enkelt enhetsbyte", Icon: Smartphone, testid: "nav-mobile" },
  { to: "/digitala-las", label: "Digitalt lås", Icon: Lock, testid: "nav-lock" },
];

const PRIMARY_AFTER = [
  { to: "/boka", label: "Boka tid", Icon: CalendarDays, testid: "nav-booking" },
  { to: "/om-oss", label: "Om oss", Icon: Users, testid: "nav-about" },
  { to: "/kontakt", label: "Kontakt", Icon: Mail, testid: "nav-contact" },
  { to: "/hjalp", label: "Hjälp", Icon: HelpCircle, testid: "nav-help" },
];

export const Header = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isActive = (to) => location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
  const anyServiceActive = SERVICES.some((s) => isActive(s.to));

  useEffect(() => { setServicesOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setServicesOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const linkCls = (active) =>
    `inline-flex items-center gap-1.5 px-3 py-3 rounded-lg text-base font-semibold whitespace-nowrap transition-colors ${active ? "text-[#0056D2]" : "text-[#1A1A1A] hover:text-[#0056D2]"}`;

  return (
    <header className="sticky top-0 z-50 bg-white border-b-2 border-gray-200">
      <div className="max-w-6xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between gap-2">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 md:gap-3 shrink-0" onClick={() => setOpen(false)}>
          <span className="bg-[#0056D2] text-white rounded-lg p-2 flex items-center justify-center">
            <BookOpen size={26} strokeWidth={2.5} />
          </span>
          <span className="font-heading text-lg md:text-2xl font-extrabold text-[#1A1A1A] whitespace-nowrap">Enkla Manualer</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          <Link to="/" data-testid="nav-home" className={linkCls(location.pathname === "/")}>
            <Home size={20} strokeWidth={2.5} /> Hem
          </Link>

          {/* Tjänster dropdown */}
          <div ref={dropdownRef} className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
            <button data-testid="nav-services" aria-expanded={servicesOpen} onClick={() => setServicesOpen((v) => !v)}
              className={linkCls(anyServiceActive)}>
              Tjänster <ChevronDown size={18} strokeWidth={2.5} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div data-testid="services-menu" className="absolute left-0 top-full pt-2 w-64 z-50">
                <div className="bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden py-1">
                  {SERVICES.map(({ to, label, Icon, testid }) => (
                    <Link key={to} to={to} data-testid={testid} onClick={() => setServicesOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 text-base font-semibold transition-colors ${isActive(to) ? "bg-blue-50 text-[#0056D2]" : "text-[#1A1A1A] hover:bg-gray-50"}`}>
                      <Icon size={20} strokeWidth={2.5} className="text-[#0056D2] shrink-0" /> {label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {PRIMARY_AFTER.map(({ to, label, Icon, testid }) => (
            <Link key={to} to={to} data-testid={testid} className={linkCls(isActive(to))}>
              <Icon size={20} strokeWidth={2.5} /> {label}
            </Link>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button data-testid="menu-toggle" aria-label="Öppna meny" aria-expanded={open} onClick={() => setOpen(!open)}
          className="lg:hidden inline-flex items-center justify-center min-h-[52px] min-w-[52px] rounded-lg border-2 border-gray-300 text-[#1A1A1A]">
          {open ? <X size={28} strokeWidth={2.5} /> : <Menu size={28} strokeWidth={2.5} />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <nav data-testid="mobile-menu" className="lg:hidden border-t-2 border-gray-200 bg-white px-4 py-3">
          <Link to="/" data-testid="m-nav-home" onClick={() => setOpen(false)}
            className={`flex items-center gap-3 min-h-[60px] px-4 rounded-lg text-xl font-semibold transition-colors ${location.pathname === "/" ? "bg-blue-50 text-[#0056D2]" : "text-[#1A1A1A] hover:bg-gray-50"}`}>
            <Home size={26} strokeWidth={2.5} /> Hem
          </Link>

          <p className="px-4 pt-4 pb-1 text-sm font-bold uppercase tracking-wide text-[#4A4A4A]">Tjänster</p>
          {SERVICES.map(({ to, label, Icon, testid }) => (
            <Link key={to} to={to} data-testid={`m-${testid}`} onClick={() => setOpen(false)}
              className={`flex items-center gap-3 min-h-[60px] px-4 rounded-lg text-xl font-semibold transition-colors ${isActive(to) ? "bg-blue-50 text-[#0056D2]" : "text-[#1A1A1A] hover:bg-gray-50"}`}>
              <Icon size={26} strokeWidth={2.5} /> {label}
            </Link>
          ))}

          <div className="border-t-2 border-gray-100 mt-3 pt-3">
            {PRIMARY_AFTER.map(({ to, label, Icon, testid }) => (
              <Link key={to} to={to} data-testid={`m-${testid}`} onClick={() => setOpen(false)}
                className={`flex items-center gap-3 min-h-[60px] px-4 rounded-lg text-xl font-semibold transition-colors ${isActive(to) ? "bg-blue-50 text-[#0056D2]" : "text-[#1A1A1A] hover:bg-gray-50"}`}>
                <Icon size={26} strokeWidth={2.5} /> {label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};
